/**
 * Sharetribe Marketplace API exposes one default payment method per user.
 *
 * Endpoints: stripeCustomer.create, addPaymentMethod, deletePaymentMethod.
 * Read path: currentUser.stripeCustomer.defaultPaymentMethod.
 *
 * There is no list of cards, no set-default, and no “choose at checkout”
 * among saved methods. Checkout charges the default, or a one-time card
 * for that booking only. Extra cards cannot be stored and picked later
 * through Sharetribe’s model — do not build a multi-card wallet on top.
 *
 * Swap order: the new Stripe PaymentMethod id must exist first
 * (SetupIntent / confirmCardSetup). Then delete the old default and
 * add the new id. addPaymentMethod fails while a default is still set,
 * so both cards cannot stay attached in Sharetribe.
 */

/**
 * @param {string} [stripePaymentMethodId]
 * @returns {boolean}
 */
export const canSwapDefaultPaymentMethod = stripePaymentMethodId =>
  typeof stripePaymentMethodId === 'string' && stripePaymentMethodId.startsWith('pm_');

/**
 * Delete the current default only after a new Stripe PM id is in hand.
 *
 * @param {Object} params
 * @param {string} params.stripePaymentMethodId
 * @param {Function} params.remove
 * @param {Function} params.add
 * @returns {Promise<*>}
 */
export const replaceDefaultPaymentMethod = async ({
  stripePaymentMethodId,
  remove,
  add,
}) => {
  if (!canSwapDefaultPaymentMethod(stripePaymentMethodId)) {
    throw new Error('Save the new card before removing the current one.');
  }
  await remove();
  return add(stripePaymentMethodId);
};
